/**
 * Global state management with Zustand
 */

import { create } from 'zustand';

const useAppStore = create((set, get) => ({
  // Watchlist data
  allSignals: [],
  tickers: [],
  isLoading: false,
  hasMore: true,
  totalTickers: 0,

  // Selected ticker
  selectedTicker: null,
  selectedTickerData: null,

  // Filters
  filter: 'ALL', // 'ALL' | 'BUY' | 'SELL'
  searchQuery: '',
  market: 'US', // 'US' | 'IN' (Nifty 500)

  // Sorting
  sortColumn: 'symbol',
  sortDirection: 'asc',

  // Cache status
  cacheVersion: null,
  lastUpdated: null,

  // Actions
  setAllSignals: (signals) => set({ allSignals: signals }),

  setTickers: (tickers) => set({ tickers }),

  addTickers: (newTickers) => set((state) => ({
    tickers: [...state.tickers, ...newTickers]
  })),

  setLoading: (isLoading) => set({ isLoading }),

  setHasMore: (hasMore) => set({ hasMore }),

  setTotalTickers: (total) => set({ totalTickers: total }),

  setSelectedTicker: (ticker, data = null) => set({
    selectedTicker: ticker,
    selectedTickerData: data
  }),

  setFilter: (filter) => set({ filter }),

  setSearchQuery: (query) => set({ searchQuery: query }),

  setMarket: (market) => set({ market }),

  setSort: (column) => set((state) => ({
    sortColumn: column,
    sortDirection: state.sortColumn === column && state.sortDirection === 'asc' ? 'desc' : 'asc'
  })),

  setCacheVersion: (version) => set({ cacheVersion: version }),

  setLastUpdated: (timestamp) => set({ lastUpdated: timestamp }),

  // Computed getters
  getFilteredTickers: () => {
    const { tickers, filter, searchQuery } = get();

    let filtered = tickers;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t =>
        t.ticker.toLowerCase().includes(query)
      );
    }

    // Apply signal filter
    if (filter === 'BUY') {
      filtered = filtered.filter(t => t.buy_count > 0);
    } else if (filter === 'SELL') {
      filtered = filtered.filter(t => t.sell_count > 0);
    }

    return filtered;
  },

  getSortedTickers: () => {
    const { sortColumn, sortDirection } = get();
    const filtered = get().getFilteredTickers();

    const sorted = [...filtered].sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      // Handle null/undefined
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      // Compare
      if (typeof aVal === 'string') {
        return aVal.localeCompare(bVal);
      }
      return aVal - bVal;
    });

    return sortDirection === 'desc' ? sorted.reverse() : sorted;
  },

  // Reset all state
  reset: () => set({
    allSignals: [],
    tickers: [],
    isLoading: false,
    hasMore: true,
    totalTickers: 0,
    selectedTicker: null,
    selectedTickerData: null,
    filter: 'ALL',
    searchQuery: '',
    market: 'US',
    sortColumn: 'symbol',
    sortDirection: 'asc'
  })
}));

export default useAppStore;
